import React, { useState, useEffect } from 'react'
import { Button, Icon, Spin, Modal } from 'antd'
import { useWardrobe } from '../../../../hooks/useWardrobe'
import { QuickOutfitSelect } from './shared'

/**
 * Self-fetching outfit card for selected/mapped outfits.
 *
 * Instead of relying on the paginated outfit list (which may not contain
 * the selected outfit), this component fetches its own data using the
 * "get" action with the outfit tag.
 *
 * @param {Object} props
 * @param {String} props.outfitKey - The outfit placeholder key (e.g. "__casual__")
 * @param {String} props.tag - The outfit tag currently assigned
 * @param {String} props.description - Human-readable outfit description
 * @param {String} props.initialImage - Optional pre-loaded image URL
 * @param {Array} props.libraryOutfits - Outfits from the library for QuickSelect
 * @param {Function} props.onImageResolved - (outfitKey, imageUrl) => void
 * @param {Function} props.onQuickSelect - (outfitKey, outfit) => void
 * @param {Function} props.onOpenPicker - (outfitKey, mode) => void
 * @param {Function} props.onOpenCreate - (outfitKey) => void
 * @param {String} props.videoId - Video ID for API calls
 */
const SelectedOutfitCard = ({
  outfitKey,
  tag,
  description,
  initialImage,
  libraryOutfits = [],
  onImageResolved,
  onOutfitFetched,
  onQuickSelect,
  onOpenPicker,
  onOpenCreate,
  onEditOutfit,
  onDeleteOutfit,
  videoId
}) => {
  const [image, setImage] = useState(initialImage || null)
  const [loading, setLoading] = useState(false)
  const [outfitData, setOutfitData] = useState(null)
  const [fetchError, setFetchError] = useState(false)

  const { getOutfit } = useWardrobe({ videoId })

  const cleanOutfitLabel = outfitKey.replace(/^_{2}|_{2}$/g, '')

  // Core fetch logic — extracted so retry can reuse it
  const fetchOutfitFromApi = async () => {
    setFetchError(false)
    setLoading(true)
    try {
      const result = await getOutfit(tag)
      if (result) {
        const resolvedImage =
          result.primary_image ||
          (result.views && Object.values(result.views)[0])
        setOutfitData(result)
        if (onOutfitFetched) onOutfitFetched(result)
        if (resolvedImage) {
          setImage(resolvedImage)
          if (onImageResolved) onImageResolved(outfitKey, resolvedImage)
        }
      }
    } catch (err) {
      console.error(`Failed to fetch outfit for tag "${tag}":`, err)
      setFetchError(true)
    } finally {
      setLoading(false)
    }
  }

  // Fetch outfit data by tag — first check libraryOutfits, then fallback to API
  useEffect(() => {
    let cancelled = false

    const resolveOutfit = async () => {
      if (!tag || image) return

      // Check if outfit already exists in the loaded library
      const existing = libraryOutfits.find(o => (o.tag || o.id) === tag)
      if (existing) {
        const existingImage =
          existing.primary_image ||
          (existing.views && Object.values(existing.views)[0])
        setOutfitData(existing)
        if (existingImage) {
          setImage(existingImage)
          if (onImageResolved) onImageResolved(outfitKey, existingImage)
        }
        return
      }

      // Not in local list — fetch via API
      setFetchError(false)
      setLoading(true)
      try {
        const result = await getOutfit(tag)
        if (cancelled) return

        if (result) {
          const resolvedImage =
            result.primary_image ||
            (result.views && Object.values(result.views)[0])
          setOutfitData(result)
          if (onOutfitFetched) onOutfitFetched(result)
          if (resolvedImage) {
            setImage(resolvedImage)
            if (onImageResolved) onImageResolved(outfitKey, resolvedImage)
          }
        }
      } catch (err) {
        console.error(`Failed to fetch outfit for tag "${tag}":`, err)
        if (!cancelled) setFetchError(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    resolveOutfit()
    return () => {
      cancelled = true
    }
  }, [tag]) // Only re-run when tag changes

  // Sync external image changes (e.g. from picker or create)
  useEffect(() => {
    if (initialImage) setImage(initialImage)
  }, [initialImage])

  return (
    <div
      className="outfit-item-card clickable"
      onClick={() => onOpenPicker && onOpenPicker(outfitKey, 'view')}
    >
      {/* ── Preview area ── */}
      <div className="outfit-preview-area">
        {loading ? (
          <div className="outfit-loading-state">
            <Spin size="small" />
            <span>Loading outfit…</span>
          </div>
        ) : fetchError ? (
          // Error state — keeps the "selected" card shape, shows error + retry + Create/Library
          <div className="outfit-error-state">
            <div className="error-top">
              <Icon type="warning" className="error-icon" />
              <span className="error-message">Couldn't load outfit</span>
              <Button
                className="retry-btn"
                size="small"
                onClick={e => {
                  e.stopPropagation()
                  fetchOutfitFromApi()
                }}
              >
                <Icon type="reload" /> Retry
              </Button>
            </div>

            <div className="error-divider" />

            <div className="actions-area">
              <div
                className="action-item"
                onClick={e => {
                  e.stopPropagation()
                  if (onOpenCreate) onOpenCreate(outfitKey)
                }}
              >
                <Icon type="plus-circle" />
                <span>Create</span>
              </div>
              <div
                className="action-item"
                onClick={e => {
                  e.stopPropagation()
                  if (onOpenPicker) onOpenPicker(outfitKey, 'select')
                }}
              >
                <Icon type="appstore" />
                <span>Library</span>
              </div>
            </div>
          </div>
        ) : image ? (
          <div className="image-preview-wrapper">
            <img src={image} alt={cleanOutfitLabel} />

            <div className="image-overlay">
              <div className="overlay-actions main-actions">
                <Button
                  className="same-width-btn"
                  type="primary"
                  onClick={e => {
                    e.stopPropagation()
                    if (onOpenCreate) onOpenCreate(outfitKey)
                  }}
                >
                  Create New
                </Button>
                <Button
                  className="same-width-btn"
                  type="default"
                  onClick={e => {
                    e.stopPropagation()
                    if (onOpenPicker) onOpenPicker(outfitKey, 'select')
                  }}
                >
                  Library
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="upload-trigger-container empty">
            <div className="actions-area">
              <div
                className="action-item"
                onClick={e => {
                  e.stopPropagation()
                  if (onOpenCreate) onOpenCreate(outfitKey)
                }}
              >
                <Icon type="plus-circle" />
                <span>Create</span>
              </div>
              <div
                className="action-item"
                onClick={e => {
                  e.stopPropagation()
                  if (onOpenPicker) onOpenPicker(outfitKey, 'select')
                }}
              >
                <Icon type="appstore" />
                <span>Library</span>
              </div>
            </div>

            <div className="quick-select-area">
              <QuickOutfitSelect
                outfits={libraryOutfits}
                onSelect={outfit =>
                  onQuickSelect && onQuickSelect(outfitKey, outfit)
                }
                placeholder="Quick Select..."
                size="default"
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Footer / details ── */}
      <div className="outfit-details">
        {/* Top row: label badge + action pill */}
        <div className="outfit-details-top">
          <span className="outfit-label">{cleanOutfitLabel}</span>

          {(onEditOutfit || onDeleteOutfit) && (
            <div className="outfit-action-pill">
              {onEditOutfit && (
                <button
                  className="pill-btn pill-btn--edit"
                  title="Edit outfit"
                  onClick={e => {
                    e.stopPropagation()
                    onEditOutfit(outfitKey, description)
                  }}
                >
                  <Icon type="edit" />
                </button>
              )}
              {onEditOutfit && onDeleteOutfit && (
                <span className="pill-divider" />
              )}
              {onDeleteOutfit && (
                <button
                  className="pill-btn pill-btn--delete"
                  title="Delete outfit"
                  onClick={e => {
                    e.stopPropagation()
                    onDeleteOutfit(outfitKey)
                  }}
                >
                  <Icon type="delete" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Title + description */}
        {description.includes(':') ? (
          <>
            <div className="outfit-title">
              {description.split(':')[0].trim()}
            </div>
            <div className="outfit-description">
              {description
                .split(':')
                .slice(1)
                .join(':')
                .trim()}
            </div>
          </>
        ) : (
          <div className="outfit-description">{description}</div>
        )}
      </div>
    </div>
  )
}

export default SelectedOutfitCard
